import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useState, type RefCallback, type RefObject } from "react";

import type { ApiSchemas } from "@/shared/api/schema";

import { NotificationCard } from "./notification-card";
import { NotificationListSkeleton } from "./notification-card-skeleton";

type Notification = ApiSchemas["Notification"];

type NotificationListProps = {
  notifications: Notification[];
  onNotificationClick: (notification: Notification) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  cursorRef?: RefCallback<HTMLDivElement>;
  isFetchingNextPage?: boolean;
  savedScrollOffset?: number;
};

export function NotificationList({
  notifications,
  onNotificationClick,
  scrollContainerRef,
  cursorRef,
  isFetchingNextPage,
  savedScrollOffset = 0,
}: NotificationListProps) {
  const [isReady, setIsReady] = useState(false);

  // Wait for scroll container to be ready after drawer animation
  useEffect(() => {
    if (scrollContainerRef.current) {
      const timer = requestAnimationFrame(() => {
        setIsReady(true);
      });
      return () => cancelAnimationFrame(timer);
    }
  }, [scrollContainerRef]);

  // Restore scroll position after mounting
  useEffect(() => {
    if (isReady && savedScrollOffset > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollOffset;
    }
  }, [isReady, savedScrollOffset, scrollContainerRef]);

  const virtualizer = useVirtualizer({
    count: notifications.length,
    getScrollElement: () => (isReady ? scrollContainerRef.current : null),
    estimateSize: () => 76,
    overscan: 5,
    initialOffset: savedScrollOffset,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const itemHeight = 76;
  const totalHeight = notifications.length * itemHeight;

  // Use virtual items if available, otherwise create fallback items
  const itemsToRender =
    virtualItems.length > 0
      ? virtualItems
      : notifications.map((_, i) => ({
          index: i,
          start: i * itemHeight,
          size: itemHeight,
        }));

  return (
    <div className="p-2">
      <div
        style={{
          height: virtualizer.getTotalSize() || totalHeight,
          width: "100%",
          position: "relative",
        }}
      >
        {itemsToRender.map((row) => {
          const notification = notifications[row.index];
          return (
            <div
              key={notification.id}
              ref={virtualizer.measureElement}
              data-index={row.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${row.start}px)`,
              }}
            >
              <NotificationCard
                notification={notification}
                onClick={() => onNotificationClick(notification)}
              />
            </div>
          );
        })}
      </div>
      {/* Cursor for infinite scroll */}
      {cursorRef && <div ref={cursorRef} className="h-2" />}
      {/* Loading indicator for next page */}
      {isFetchingNextPage && <NotificationListSkeleton count={3} />}
    </div>
  );
}
